import React, {useEffect, useRef} from 'react';
import {useParams, useLocation, useNavigate} from 'react-router-dom';
import {useSelector} from 'react-redux';
import {menuAccount} from '../../layout/menuAccount';
import {menuEvent} from '../../layout/menu';
import {hasAccountRoleAccess, hasEventRoleAccess, isMaster} from '../../functions/permission';
import Page404 from '../../pages/page404/Page404';
import MyIframe from '../../layout/myIframe/MyIframe';
import SeatmapManage from '../../pages/tickets/seatmapManage/SeatmapManage';
import {newBrandingOldEvent} from '../../functions/customSystemPixel/customSystemPixel';
import {REACT_APP_ENV, TICK_DOMAIN_MY, IS_NEW_TIMETABLE_UI} from '../../config/config';
import setGlobalState from '../../redux/setGlobalState';
import {match} from 'path-to-regexp';
import useLoadingState from '../../hooks/useLoadingState';
import TickchakLoader from '../../component/tickchakLoader/TickchakLoader';
import {EVENT_TYPES} from '../../redux/event/event.type';
import {logout} from '../../redux/user/userActions';
import {dispatchEvent} from '../../hooks/dispatchEvent';
import getPathByEventRole from '../../functions/getPathByEventRole';

const findMenuItem = (menu, pathKey, type) => {
    for(const key in menu) {
        const route = menu[key];
        if(!route.path || typeof route.path !== 'string') {
            // Skip invalid or undefined paths
            continue;
        }
        if(route.events && !route.events?.includes(type)) {
            continue;
        }
        // Match exact paths
        if(route.path === pathKey) {
            return route;
        }

        // Match dynamic paths using path-to-regexp's match function
        const matcher = match(route.path);
        const matchResult = matcher(pathKey);
        if(matchResult) {
            return route;
        }
    }
    return null;
};
// Global reference to iframe ref for iframeClick function
let globalIframeRef = null;

export const iframeClick = (buttonClick, type, props = {}) => {
    const iFrame = globalIframeRef?.current?.contentWindow;
    // const iFrame = window?.frames?.length && window.frames[window.frames.length - 1];
    if(iFrame){
        iFrame.postMessage({buttonClick: buttonClick, type: type, props}, TICK_DOMAIN_MY);
    }
    if(buttonClick==='logout'){
        logout().then(()=>{
            dispatchEvent('401Exception');
        }).catch(()=>{
            dispatchEvent('401Exception');
        });
    }
};
// Component to handle menu-based route rendering with permissions
const RouteComponent = () => {
    const {pid, page, eid, subPage} = useParams();
    const event = useSelector((s:any) => s.event);
    const isScheduled = useSelector((s: any) => s.ui.event.isScheduled);
    const duplicateSourceEid = useSelector((s: any) => s.ui.event.duplicateSourceEid);
    const oldSystem = useSelector((s: any) => s.realtime.user.oldSystem);
    const location = useLocation();
    const navigate = useNavigate();
    const isWait = useLoadingState(eid, pid);
    const menu = pid ? menuAccount : menuEvent;
    const pathKey = location.pathname.split('/').slice(3).join('/');
    const menuItem = findMenuItem(menu, `/${pathKey}`, event.type) || menu[page];
    const iframeRef = useRef(null);
    const pathLocation = location.pathname.split('/');

    // Update global reference for iframeClick function
    globalIframeRef = iframeRef;

    // Helper functions from Layout for special cases
    const showInIframe = (path) => {
        switch (path) {
            case '/event':
                return ![
                    EVENT_TYPES.TICKET,
                    EVENT_TYPES.AREA,
                    EVENT_TYPES.HUB,
                    EVENT_TYPES.GIVECHAK,
                    EVENT_TYPES.TIMETABLE,
                    EVENT_TYPES.SUBSCRIPTION
                ].includes(event.type) &&
                       !isScheduled && !duplicateSourceEid;
            case '/tickets/branding/:pageId':
            case '/tickets/branding/:pageId/:messageType':
            case '/courses/branding/:pageId':
            case '/courses/branding/:pageId/:messageType':
                return (REACT_APP_ENV === 'production' && event.eid <= 58410 &&
                       !newBrandingOldEvent(event.eid));
            case '/realtime':
                return oldSystem || (event?.type === EVENT_TYPES.TIMETABLE && !IS_NEW_TIMETABLE_UI(event.eid));
            case '/timetable':
                return !IS_NEW_TIMETABLE_UI(event.eid);
            default:
                return false;
        }
    };

    const showTicketsInIframe = () => {
        return event?.type === 'givechak' ||
               (event?.type === 'timetable' && !IS_NEW_TIMETABLE_UI(event.eid)) ||
               isScheduled ||
               event?.hasScheduledOrPriceFloatTicket ||
               event?.hasScheduledTicket;
    };

    // Set iframeDisplay state based on component type
    const setIframeDisplay = (value) => {
        setGlobalState('ui.layout.iframeDisplay', value);
    };

    useEffect(() => {
        if(page === 'tickets' && showTicketsInIframe() && subPage !== 'branding') {
            setIframeDisplay(true);
        } else if(menuItem?.component === 'MyIframe') {
            setIframeDisplay(true);
        } else {
            setIframeDisplay(false);
        }
    }, [page, subPage]);

    useEffect(() => {
        if(!event.eid)
            return;
        if((!pathLocation[1]|| (pathLocation[2] && !pathLocation[3]))) {
            const defaultEventPath=getPathByEventRole(event);
            navigate(defaultEventPath);
        }
    }, [event.eid]);

    if(isWait) {
        return <TickchakLoader showClass={' show'} cssStyle={undefined}/>;
    }

    // Check for special cases first
    if(page === 'tickets' && !subPage && showTicketsInIframe()) {
        setIframeDisplay(true);
        return <MyIframe display={true} location={undefined} iframeRef={iframeRef} />;
    }

    if(showInIframe(`/${page}`)) {
        setIframeDisplay(true);
        return <MyIframe display={true} location={undefined} iframeRef={iframeRef} />;
    }

    // Then check menu configuration
    // If no menu item is found, return 404
    if(!menuItem) {
        setIframeDisplay(false);
        return <Page404 eidParam={eid} pidParam={pid} />;
    }

    // Permission checks
    if(pid && !hasAccountRoleAccess(menuItem.access)) {
        return <Page404 eidParam={eid} pidParam={pid} />;
    }

    if(eid) {
        // Special master check for some paths — with an opt-in escape hatch:
        // menuItem.allowedPids lets specific packages bypass the master check.
        if(menuItem.access === 'master' && !isMaster()) {
            const eventPid = (event as any)?.pid;
            const allowed  = Array.isArray((menuItem as any).allowedPids)
                && (menuItem as any).allowedPids.includes(eventPid);
            if(!allowed) {
                return <Page404 eidParam={eid} pidParam={pid} />;
            }
        }

        if(!hasEventRoleAccess(menuItem.access)) {
            // Same opt-in: master-gated routes that allow specific pids bypass
            // the broader role check too (the role check would otherwise also
            // reject non-masters since 'master' isn't in their permissions).
            const eventPid = (event as any)?.pid;
            const allowed  = menuItem.access === 'master'
                && Array.isArray((menuItem as any).allowedPids)
                && (menuItem as any).allowedPids.includes(eventPid);
            if(!allowed) {
                return <Page404 eidParam={eid} pidParam={pid} />;
            }
        }

        // Event-specific checks (from original code)
        if(menuItem.events && !menuItem.events.includes(event.type)) {
            return <Page404 eidParam={eid} pidParam={pid} />;
        }
    }

    // Render component based on type
    if(menuItem.component === 'MyIframe') {
        // NEW-format seatmap event: /tickets/seats renders the native seat
        // manager (render/select/tickets/block/reserve on smap) instead of the
        // legacy "connect a hall" iframe.
        const eventMap = (event as any)?.map;
        if(pathKey === 'tickets/seats' && eventMap?.isNew && eventMap?.smid) {
            return <SeatmapManage eid={(event as any).eid} />;
        }
        return <MyIframe display={true} location={undefined} iframeRef={iframeRef} />;
    }
    // For React components, don't show iframe
    return <>
        <menuItem.component />
        <MyIframe display={false} location={undefined} iframeRef={iframeRef} />
    </>;
};

export default RouteComponent;
